"use client";

import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { useNavigate, useSearchParams } from "react-router-dom";

import { getLoyaltyCardDetails } from "../components/auth/firestore/dynamicStoreDetails";
import { useParams } from "react-router-dom";
import { UserContext } from "../contexts/user-context";
import { addToWallet } from "../api/wallet/addToWallet";
import { Close } from "@mui/icons-material";
// import { requestFcmToken } from "../lib/utils/requestFcmToken";

interface AddtoWalletStoreFrontProps {
  shortID: string;
}

const AddtoWalletStoreFront: React.FC<AddtoWalletStoreFrontProps> = () => {
  const navigate = useNavigate();
  const { shortID } = useParams<{ shortID: string }>();
  const context = React.useContext(UserContext);

  const [cardDetails, setCardDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRewards, setShowRewards] = useState(false);

  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // const [fcmToken, setFcmToken] = useState<string | null>(null);

  const [formDisabled, setFormDisabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState(
    searchParams.get("ref") || ""
  );
  const [showReferralInput, setShowReferralInput] = useState(false);
  // eslint-disable-next-line
  const [showRefError, setShowRefError] = useState(false);
  const [referralError, setReferralError] = useState<null | string>(null);
  const [confirmOverride, setConfirmOverride] = useState(false);

  // Replace this with your actual condition check
  const referralProgramActive = cardDetails?.referralProgramActive;
  const incorrectReferralLength =
    referralCode.length > 0 && referralCode.length !== 8;

  // Only update referral state if active
  useEffect(() => {
    if (referralProgramActive) {
      const ref = searchParams.get("ref") || "";
      setReferralCode(ref);
      setShowReferralInput(!!ref);
    }
  }, [referralProgramActive, searchParams]);

  useEffect(() => {
    setShowRefError(incorrectReferralLength);
  }, [incorrectReferralLength]);

  useEffect(() => {
    if (shortID) {
      getLoyaltyCardDetails(shortID)
        .then((details: any) => {
          setCardDetails(details);
          setLoading(false);
        })
        .catch((error: Error) => {
          console.error("Error fetching loyalty card details:", error);
          setLoading(false);
        });
    }
  }, [shortID]);

  // get FCM token once (ignore failures / permission denied)
  // useEffect(() => {
  //   requestFcmToken()
  //     .then(setFcmToken)
  //     .catch((e) => console.error("FCM token error:", e));
  // }, []);

  const autoAdd = searchParams.get("autoAdd") === "1";

  useEffect(() => {
    if (
      autoAdd && // we came back from sign-up
      context?.user && // user is now logged-in
      cardDetails && // we already loaded the card
      !formSubmitted && // haven’t fired once
      !formDisabled
    ) {
      (async () => {
        try {
          setFormSubmitted(true);
          setFormDisabled(true);
          await addToWallet({
            storeID: cardDetails.storeID,
            cardDocId: cardDetails.id,
            referralCode: referralCode || null,
          });
          navigate("/wallet", { replace: true });
        } catch (err) {
          setFormDisabled(false);
          setFormSubmitted(false);
        }
      })();
    }
  }, [
    autoAdd,
    context?.user,
    cardDetails,
    formSubmitted,
    formDisabled,
    referralCode,
    navigate,
  ]);

  const handleAddToWallet = async (event: React.FormEvent) => {
    event.preventDefault();
    if (incorrectReferralLength) {
      setReferralError("Referral code must be at least 8 characters.");
      return;
    }
    setFormDisabled(true);
    setFormSubmitted(true);

    try {
      await addToWallet({
        storeID: cardDetails?.storeID || "",
        cardDocId: cardDetails?.id || "",
        referralCode: referralCode || null,
      });
      navigate("/wallet");
    } catch (err: any) {
      // Cloud Function error handling
      const code = err?.details ?? err?.message;

      if (code === "invalidLength") {
        setReferralError("Referral code must be at least 8 characters.");
      } else if (code === "codeNotFound") {
        setReferralError("Referral code not found.");
      } else if (code === "refereeNotCustomer") {
        setReferralError("Referee must be a customer of this store.");
      } else {
        // unknown error: show generic toast or dialog
        setReferralError("Something went wrong. Please try again.");
      }

      setFormDisabled(false);
    }
  };

  // AddtoWalletStoreFront.tsx
  const handleRegister = () => {
    const base = `/card/${shortID}`;
    const qs = new URLSearchParams({
      autoAdd: "1",
      ...(referralCode && { ref: referralCode }),
    });

    navigate(`/auth/sign-up?redirect=${encodeURIComponent(`${base}?${qs}`)}`, {
      replace: true,
    });
  };
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!cardDetails) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <Card sx={{ padding: 2 }}>
          <Typography variant="h6">Loyalty Card not found!</Typography>
        </Card>
      </Box>
    );
  }

  return (
    <HelmetProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh",
          padding: 2,
        }}
      >
        <Helmet>
          <meta charSet="utf-8" />
          <title>{cardDetails.storeName} | Stomp Loyalty</title>
          <meta
            name="keywords"
            content={`stomp, stomp card, loyalty card, store, rewards, stamp card, ${cardDetails.storeName}`}
          />{" "}
        </Helmet>
        <Card
          sx={{
            maxWidth: 600,
            boxShadow: 3,
            marginBottom: 3,
          }}
        >
          {cardDetails.titleImage && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                maxWidth: "95vw",
                maxHeight: "40vh",
                aspectRatio: "2/1",
              }}
            >
              <CardMedia
                component="img"
                image={cardDetails.titleImage}
                alt={`${cardDetails.storeName} Image`}
                sx={{ borderRadius: "4px 4px 0 0" }}
              />
            </Box>
          )}
          <CardContent
            sx={{
              paddingTop: 2,
              paddingBottom: 2,
              backgroundColor: cardDetails.cardBackgroundColor,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="h5"
                component="div"
                gutterBottom
                sx={{ color: cardDetails.cardTextColor, fontWeight: 600 }}
              >
                {cardDetails.storeName}
              </Typography>
            </Box>

            {cardDetails.cardDescription && (
              <Typography
                variant="body2"
                color={cardDetails.cardTextColor}
                gutterBottom
              >
                {cardDetails.cardDescription}
              </Typography>
            )}
            {cardDetails.rewards && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: cardDetails.cardTextColor,
                    cursor: "pointer",
                    marginTop: 1,
                    marginBottom: 0,
                  }}
                  onClick={() => setShowRewards(!showRewards)}
                >
                  <Typography
                    variant="body1"
                    sx={{ textDecoration: "underline" }}
                  >
                    Rewards
                  </Typography>{" "}
                </Box>
                <Collapse in={showRewards}>
                  <Box
                    sx={{
                      marginTop: -1,
                      color: cardDetails.cardTextColor,
                      fontSize: "0.8rem",
                    }}
                  >
                    <ul>
                      {cardDetails.rewards
                        .sort(
                          (
                            a: { balanceRequired: number },
                            b: { balanceRequired: number }
                          ) => a.balanceRequired - b.balanceRequired
                        )
                        .map(
                          (
                            reward: {
                              balanceRequired: number;
                              reward: string;
                            },
                            index: number
                          ) => (
                            <li key={index}>
                              {reward.balanceRequired}{" "}
                              {cardDetails.cardType === "pointCard"
                                ? "Points"
                                : "Scans"}{" "}
                              - {reward.reward}
                            </li>
                          )
                        )}
                    </ul>
                  </Box>
                </Collapse>
              </>
            )}
            {cardDetails.disclaimer && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "grey",
                    cursor: "pointer",
                    marginTop: 1,
                    marginBottom: 0,
                  }}
                  onClick={() => setShowDisclaimer(!showDisclaimer)}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ textDecoration: "underline" }}
                  >
                    Terms and Conditions
                  </Typography>{" "}
                  *
                </Box>
                <Collapse in={showDisclaimer}>
                  <Box
                    sx={{
                      marginTop: 0,
                      color: "dark-grey",
                      fontSize: "0.8rem",
                    }}
                  >
                    <ul>
                      {cardDetails.disclaimer
                        .split("\n")
                        .map((point: any, index: number) => (
                          <li key={index}>{point}</li>
                        ))}
                    </ul>
                  </Box>
                </Collapse>
              </>
            )}
          </CardContent>
          <Card
            sx={{
              maxWidth: 600,
              boxShadow: 3,
              padding: 2,
              backgroundColor: cardDetails.cardBackgroundColor,
            }}
          >
            <Typography variant="h5" component="div" gutterBottom>
              Get {cardDetails.cardName}
            </Typography>
            {cardDetails.referralProgramActive && (
              <Box>
                {showReferralInput ? (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="Referral code (optional)"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      disabled={formDisabled}
                      error={incorrectReferralLength}
                      helperText={
                        incorrectReferralLength
                          ? "Referral code must be 8 characters."
                          : " "
                      }
                    />

                    <IconButton
                      aria-label="hide referral"
                      onClick={() => setShowReferralInput(false)}
                      size="small"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      cursor: "pointer",
                      textDecoration: "underline",
                      mb: 1,
                    }}
                    onClick={() => setShowReferralInput(true)}
                  >
                    Have a referral code?
                  </Typography>
                )}
              </Box>
            )}
            <Box>
              {cardDetails?.active ? (
                context?.user ? (
                  <Button
                    variant="contained"
                    disabled={formDisabled || incorrectReferralLength}
                    color="secondary"
                    fullWidth
                    sx={{ borderRadius: "7px", marginTop: 2 }}
                    onClick={handleAddToWallet} // Function to add loyalty card
                  >
                    Add to Wallet
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    disabled={formDisabled}
                    color="error"
                    fullWidth
                    sx={{ borderRadius: "7px", marginTop: 2 }}
                    onClick={handleRegister} // Function to register and add loyalty card
                  >
                    Register Wallet
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  disabled
                  color="warning"
                  fullWidth
                  sx={{ borderRadius: "7px", marginTop: 2 }}
                >
                  Loyalty Program Not Active
                </Button>
              )}
            </Box>

            {formSubmitted && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ marginTop: 2 }}
              >
                Redirecting to your wallet...
              </Typography>
            )}
          </Card>
        </Card>
      </Box>
      <Dialog
        open={Boolean(referralError) && !confirmOverride}
        onClose={() => setReferralError(null)}
      >
        <DialogTitle>Invalid Referral Code</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "red", fontWeight: "bold" }}>
            {referralError}
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Do you want to continue without a referral?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setReferralError(null);
              setFormDisabled(false);
              setFormSubmitted(false);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setReferralError(null);
              // user wants to continue w/out referral
              setConfirmOverride(true);
              // try again with empty code
              addToWallet({
                storeID: cardDetails?.storeID || "",
                cardDocId: cardDetails?.id || "",
                referralCode: null,
              })
                .then(() => navigate("/wallet"))
                .catch(() => setFormDisabled(false));
            }}
            color="secondary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </HelmetProvider>
  );
};

export default AddtoWalletStoreFront;
